import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { Badge, Button, Card, CardBody, Col, Row } from "reactstrap"

import InputField from "components/Common/InputField"
import PageWrapper from "components/Common/PageWrapper"
import TableContainer from "components/Common/TableContainer"
import ThemeModal from "components/Common/ThemeModal"
import {
  useCheckIn,
  useCheckOut,
  useMyAttendanceHistory,
  useTodayAttendance,
} from "hooks/attendanceHook"
import {
  formatDuration,
  formatMinutes,
  formatReadableDateInZone,
  formatReadableTimeInZone,
} from "utils/formatDate"
import { distanceInMeters } from "utils/geo"

/**
 * Out-of-office warning shown to employees (Bangla). Attendance is never blocked
 * on distance, but a punch from outside the office requires a written reason
 * that the authority reviews. The distance value is intentionally not shown.
 */
const OUT_OF_RANGE_BN = {
  title: "অফিস এলাকার বাইরে",
  warning:
    "আপনি এখন অফিস এলাকার বাইরে আছেন। অফিসের বাইরে থেকে হাজিরা দেওয়া নিয়মবহির্ভূত। কর্তৃপক্ষ বিষয়টি দেখতে পাবে এবং যাচাই করবে।",
  instruction:
    'কেন আপনি অফিসের বাইরে থেকে হাজিরা দিচ্ছেন তা স্পষ্ট করে লিখুন। যেমনঃ "গাড়ি নিয়ে বাইরে আছি", "কোম্পানির কাজে ঢাকার বাইরে", "স্যারের নির্দেশে অমুক জায়গায়"। কারণ ছাড়া হাজিরা গ্রহণ করা হবে না।',
  placeholder: "এখানে কারণ লিখুন...",
  confirm: "কারণসহ হাজিরা দিন",
  cancel: "বাতিল",
}

const formatOfficeTime = time => {
  if (!time) return "--:--"
  const [h, m] = time.split(":")
  const date = new Date()
  date.setHours(Number(h), Number(m))
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
}

const getBrowserLocation = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser."))
      return
    }
    navigator.geolocation.getCurrentPosition(
      position =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
      () =>
        reject(
          new Error(
            "Location access denied. Please allow location access in your browser to mark attendance."
          )
        ),
      { enableHighAccuracy: true, timeout: 15000 }
    )
  })

const MyAttendance = () => {
  document.title = "My Attendance"

  const [month, setMonth] = useState(new Date().getMonth() + 1)
  const [year, setYear] = useState(new Date().getFullYear())
  const [locating, setLocating] = useState(false)
  const [now, setNow] = useState(() => new Date())
  const [pendingPunch, setPendingPunch] = useState(null)
  const [remarksText, setRemarksText] = useState("")

  const { data: todayResponse, isLoading: todayLoading } = useTodayAttendance()
  const today = todayResponse?.data
  const attendance = today?.attendance
  const settings = today?.settings

  useEffect(() => {
    if (!today?.server_time) return

    const serverStart = new Date(today.server_time).getTime()
    const localStart = Date.now()
    if (Number.isNaN(serverStart)) return

    setNow(new Date(serverStart))
    const interval = setInterval(() => {
      setNow(new Date(serverStart + (Date.now() - localStart)))
    }, 1000)

    return () => clearInterval(interval)
  }, [today?.server_time])

  const attendanceTimezone = today?.server_timezone ?? settings?.attendance_timezone

  const dutyDuration = useMemo(() => {
    if (!attendance?.check_in_at) return null
    const start = new Date(attendance.check_in_at)
    if (Number.isNaN(start.getTime())) return null
    const end = attendance.check_out_at ? new Date(attendance.check_out_at) : now
    if (Number.isNaN(end.getTime())) return null
    const totalSeconds = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000))
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return { totalSeconds, hours, minutes, seconds, isLive: !attendance.check_out_at }
  }, [attendance?.check_in_at, attendance?.check_out_at, now])

  const { data: historyResponse, isLoading: historyLoading } =
    useMyAttendanceHistory({ month, year, per_page: 31 })

  const { mutateAsync: checkIn, isPending: checkingIn } = useCheckIn()
  const { mutateAsync: checkOut, isPending: checkingOut } = useCheckOut()

  const punching = locating || checkingIn || checkingOut

  const submitPunch = useCallback(
    async (type, coords, remarks) => {
      try {
        const payload = remarks ? { ...coords, remarks } : coords
        const res = await (type === "in" ? checkIn(payload) : checkOut(payload))
        toast.success(
          res?.message ??
            (type === "in" ? "Checked in successfully." : "Checked out successfully.")
        )
      } catch (error) {
        console.error(error)
        if (error?.message && !error?.response) {
          toast.error(error.message)
        }
      }
    },
    [checkIn, checkOut]
  )

  const handlePunch = useCallback(
    async type => {
      if (!settings?.is_configured) {
        toast.error(
          "Office hours are not configured. Contact your administrator."
        )
        return
      }

      try {
        setLocating(true)
        const coords = await getBrowserLocation()

        if (
          settings.geofence_enabled &&
          settings.geofence_lat !== null &&
          settings.geofence_lat !== undefined &&
          settings.geofence_lng !== null &&
          settings.geofence_lng !== undefined
        ) {
          const distance = distanceInMeters(
            Number(settings.geofence_lat),
            Number(settings.geofence_lng),
            coords.latitude,
            coords.longitude
          )

          if (distance > settings.geofence_radius_m) {
            // Out of office area: allow the punch but collect a mandatory reason first.
            setRemarksText("")
            setPendingPunch({ type, ...coords })
            return
          }
        }

        await submitPunch(type, coords)
      } catch (error) {
        console.error(error)
        if (error?.message && !error?.response) {
          toast.error(error.message)
        }
      } finally {
        setLocating(false)
      }
    },
    [settings, submitPunch]
  )

  const confirmOutOfRangePunch = useCallback(async () => {
    if (!pendingPunch) return
    const reason = remarksText.trim()
    if (!reason) return

    const { type, latitude, longitude } = pendingPunch
    setPendingPunch(null)
    await submitPunch(type, { latitude, longitude }, reason)
  }, [pendingPunch, remarksText, submitPunch])

  const columns = useMemo(
    () => [
      {
        header: "Date",
        accessorKey: "date",
        cell: ({ row }) =>
          formatReadableDateInZone(row.original.date, attendanceTimezone, "EEE, MMM d"),
      },
      {
        header: "Check In",
        cell: ({ row }) =>
          formatReadableTimeInZone(row.original.check_in_at, attendanceTimezone),
      },
      {
        header: "Check Out",
        cell: ({ row }) =>
          formatReadableTimeInZone(row.original.check_out_at, attendanceTimezone),
      },
      {
        header: "Status",
        cell: ({ row }) => {
          const status = row.original.status
          const label = status === "late" ? "Late" : status === "present" ? "On time" : "—"
          return (
            <Badge color={status === "late" ? "danger" : "success"} className="text-uppercase">
              {label}
            </Badge>
          )
        },
      },
      {
        header: "Late / Early",
        cell: ({ row }) => {
          const late = row.original.late_minutes
          const early = row.original.early_leave_minutes
          if (!late && !early) return "—"
          return (
            <div className="small">
              {late ? (
                <div className="text-danger">Late {formatMinutes(late)}</div>
              ) : null}
              {early ? (
                <div className="text-warning">Early {formatMinutes(early)}</div>
              ) : null}
            </div>
          )
        },
      },
    ],
    [attendanceTimezone]
  )

  const items = historyResponse?.data?.data ?? []
  const paginationInfo = historyResponse?.data ?? null

  const monthOptions = useMemo(() => {
    const options = []
    const now = new Date()
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
      options.push({
        month: date.getMonth() + 1,
        year: date.getFullYear(),
        label: date.toLocaleDateString([], { month: "long", year: "numeric" }),
      })
    }
    return options
  }, [])

  return (
    <PageWrapper title="My Attendance" breadcrumbItem="My Attendance">
      <Row className="mb-4">
        <Col lg={5}>
          <Card>
            <CardBody className="text-center">
              <h6 className="text-muted mb-1">
                {formatReadableDateInZone(
                  today?.date,
                  attendanceTimezone,
                  "EEEE, MMM d, yyyy"
                )}
              </h6>
              <p className="text-muted mb-3">
                Office: {formatOfficeTime(settings?.office_start_time)} –{" "}
                {formatOfficeTime(settings?.office_end_time)}
              </p>

              <div className="d-flex align-items-center justify-content-center gap-2 mb-3 text-primary">
                <i className="mdi mdi-clock-outline" />
                <span className="font-monospace fs-5 fw-semibold">
                  {formatReadableTimeInZone(
                    now.toISOString(),
                    attendanceTimezone,
                    "hh:mm:ss a"
                  )}
                </span>
              </div>

              <Row className="mb-3">
                <Col>
                  <p className="text-muted mb-1">Check In</p>
                  <h5>
                    {formatReadableTimeInZone(
                      attendance?.check_in_at,
                      attendanceTimezone
                    )}
                  </h5>
                </Col>
                <Col>
                  <p className="text-muted mb-1">Check Out</p>
                  <h5>
                    {formatReadableTimeInZone(
                      attendance?.check_out_at,
                      attendanceTimezone
                    )}
                  </h5>
                </Col>
              </Row>

              <div className="bg-light rounded-3 p-3 mb-3">
                <p className="text-muted small mb-1 text-uppercase fw-semibold">
                  {dutyDuration?.isLive ? "On Duty" : "Duty Time"}
                </p>
                {dutyDuration ? (
                  <div>
                    <h4
                      className={`mb-1 font-monospace ${
                        dutyDuration.isLive ? "text-success" : "text-body"
                      }`}
                    >
                      {String(dutyDuration.hours).padStart(2, "0")}:
                      {String(dutyDuration.minutes).padStart(2, "0")}:
                      {String(dutyDuration.seconds).padStart(2, "0")}
                    </h4>
                    <div className="text-muted small">
                      {dutyDuration.isLive ? (
                        <span>
                          Running since{" "}
                          {formatReadableTimeInZone(
                            attendance.check_in_at,
                            attendanceTimezone,
                            "hh:mm a"
                          )}
                        </span>
                      ) : (
                        <span>{formatDuration(dutyDuration.totalSeconds)} on duty</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <h4 className="mb-0 text-muted font-monospace">—</h4>
                )}
              </div>

              {attendance && (
                <div className="mb-3">
                  <Badge
                    color={attendance.status === "late" ? "danger" : "success"}
                    className="text-uppercase"
                  >
                    {attendance.status === "late" ? "Late" : "On time"}
                  </Badge>
                  {attendance.status === "late" && attendance.late_minutes > 0 && (
                    <div className="text-danger small mt-1">
                      Late by {formatMinutes(attendance.late_minutes)}
                    </div>
                  )}
                </div>
              )}

              {settings?.geofence_enabled && (
                <p className="text-muted small">
                  <i className="bx bx-map me-1" />
                  You must be within {settings.geofence_radius_m}m of the
                  office.
                </p>
              )}

              {today?.can_check_in ? (
                <Button
                  color="success"
                  size="lg"
                  block
                  disabled={punching || todayLoading || !settings?.is_configured}
                  onClick={() => handlePunch("in")}
                >
                  {punching ? "Please wait..." : "Check In"}
                </Button>
              ) : today?.can_check_out ? (
                <Button
                  color="danger"
                  size="lg"
                  block
                  disabled={punching || todayLoading}
                  onClick={() => handlePunch("out")}
                >
                  {punching ? "Please wait..." : "Check Out"}
                </Button>
              ) : null}

              {!settings?.is_configured && !todayLoading && (
                <p className="text-warning small mt-2 mb-0">
                  Office hours are not configured yet. Contact your
                  administrator.
                </p>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col lg={7}>
          <Row className="mb-3 align-items-center">
            <Col sm={6}>
              <h5 className="mb-0">History</h5>
            </Col>
            <Col sm={6} className="text-end">
              <select
                className="form-select d-inline-block w-auto"
                value={`${year}-${month}`}
                onChange={e => {
                  const [y, m] = e.target.value.split("-")
                  setYear(Number(y))
                  setMonth(Number(m))
                }}
              >
                {monthOptions.map(option => (
                  <option
                    key={`${option.year}-${option.month}`}
                    value={`${option.year}-${option.month}`}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </Col>
          </Row>

          <TableContainer
            columns={columns}
            data={items}
            isPagination={true}
            paginationInfo={paginationInfo}
            onPageChange={() => {}}
            loading={historyLoading}
            tableClass="align-middle table-nowrap w-100"
          />
        </Col>
      </Row>

      <ThemeModal
        isOpen={!!pendingPunch}
        toggle={() => setPendingPunch(null)}
        title={OUT_OF_RANGE_BN.title}
        footer={
          <>
            <Button
              color="light"
              disabled={punching}
              onClick={() => setPendingPunch(null)}
            >
              {OUT_OF_RANGE_BN.cancel}
            </Button>
            <Button
              color="danger"
              disabled={punching || !remarksText.trim()}
              onClick={confirmOutOfRangePunch}
            >
              {checkingIn || checkingOut ? "..." : OUT_OF_RANGE_BN.confirm}
            </Button>
          </>
        }
      >
        <div className="alert alert-danger d-flex align-items-start gap-2 mb-3">
          <i className="bx bx-error-circle fs-4" />
          <span>{OUT_OF_RANGE_BN.warning}</span>
        </div>
        <p className="text-muted mb-2">{OUT_OF_RANGE_BN.instruction}</p>
        <InputField
          type="textarea"
          rows={4}
          maxLength={255}
          placeholder={OUT_OF_RANGE_BN.placeholder}
          value={remarksText}
          onChange={e => setRemarksText(e.target.value)}
          disabled={punching}
        />
      </ThemeModal>
    </PageWrapper>
  )
}

export default MyAttendance
